import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Types
import { User } from '../types';

// Utils
import { 
  broadcastUserUpdate, 
  onUserUpdate, 
  getUserFromStorage, 
  setUserInStorage,
  requestUserSync,
  onUserSyncRequest
} from '../utils/userSync';

export interface UserContextType {
  user: User | null;
  updateUser: (updatedUser: Partial<User>) => void;
  setUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
}

const DefaultUserContext: UserContextType = {
  user: null,
  updateUser: () => {},
  setUser: () => {},
  refreshUser: async () => {},
};

export const UserContext = createContext<UserContextType>(DefaultUserContext);

interface UserProviderProps {
  children: React.ReactNode;
  initialUser?: User | null;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children, initialUser = null }) => {
  const [user, setUserState] = useState<User | null>(initialUser);

  useEffect(() => {
    if (initialUser) {
      setUserInStorage(initialUser);
    }
  }, [initialUser]);

  const updateUser = useCallback((updatedUser: Partial<User>) => {
    setUserState((prevUser) => {
      if (!prevUser) return null;
      const newUser = {
        ...prevUser,
        ...updatedUser,
      };
      
      broadcastUserUpdate(updatedUser);
      
      return newUser;
    });
  }, []);

  const setUser = useCallback((newUser: User | null) => {
    setUserState(newUser);
    setUserInStorage(newUser);
    
    if (newUser) {
      broadcastUserUpdate(newUser);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await fetch('/users/profile', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUserState(userData);
      } else {
        console.error('Failed to refresh user data');
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  }, []);

  useEffect(() => {
    if (!user && typeof window !== 'undefined') {
      const initialUserData = (window as any).initialUserData;
      if (initialUserData) {
        setUserState(initialUserData);
        setUserInStorage(initialUserData);
      } else {
        const storedUser = getUserFromStorage();
        if (storedUser) {
          setUserState(storedUser as User);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    const unsubscribeUserUpdate = onUserUpdate((userUpdate) => {
      setUserState((prevUser) => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          ...userUpdate,
        };
      });
    });

    const unsubscribeSyncRequest = onUserSyncRequest(() => {
      setUserState((currentUser) => {
        if (currentUser) {
          broadcastUserUpdate(currentUser);
        }
        return currentUser;
      });
      return null;
    });

    return () => {
      unsubscribeUserUpdate();
      unsubscribeSyncRequest();
    };
  }, []);

  useEffect(() => {
    if (user) {
      requestUserSync();
    }
  }, [initialUser]);

  const contextValue: UserContextType = {
    user,
    updateUser,
    setUser,
    refreshUser,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
